import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import "./portal-layout.scss";

export default function PortalLayout() {
  const [tabIndex, setTabIndex] = useState<string>("home");
  const [cssReady, setCssReady] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Cargar scripts JS en secuencia para respetar dependencias
    const scriptSrcs = [
      "/assets/portal/vendor/bootstrap/js/bootstrap.bundle.min.js",
      "/assets/portal/vendor/aos/aos.js",
      "/assets/portal/vendor/glightbox/js/glightbox.min.js",
      "/assets/portal/vendor/purecounter/purecounter_vanilla.js",
      "/assets/portal/js/main.js",
    ];
    const scriptElements: HTMLScriptElement[] = [];

    const loadScriptsSequentially = (srcs: string[], index = 0): void => {
      if (index >= srcs.length) return;
      const script = document.createElement("script");
      script.src = srcs[index];
      script.async = false; // respetar orden
      script.onload = () => loadScriptsSequentially(srcs, index + 1);
      script.onerror = () => {
        console.warn(`[PortalLayout] No se pudo cargar: ${srcs[index]}`);
        loadScriptsSequentially(srcs, index + 1); // continuar aunque falle
      };
      document.body.appendChild(script);
      scriptElements.push(script);
    };

    loadScriptsSequentially(scriptSrcs);
    // Cargar fuentes solo para el portal
    const fontLinks = [
      { href: "https://fonts.googleapis.com", rel: "preconnect" },
      {
        href: "https://fonts.gstatic.com",
        rel: "preconnect",
        crossorigin: "anonymous",
      },
      {
        href: "https://fonts.googleapis.com/css2?family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&family=Poppins:ital,wght@0,100;0,200;0,300;0,400;0,500;0,600;0,700;0,800;0,900;1,100;1,200;1,300;1,400;1,500;1,600;1,700;1,800;1,900&display=swap",
        rel: "stylesheet",
      },
    ];
    const linkElements = fontLinks.map((props) => {
      const link = document.createElement("link");
      Object.entries(props).forEach(([key, value]) =>
        link.setAttribute(key, value)
      );
      document.head.appendChild(link);
      return link;
    });
    // Cargar estilos CSS solo para el portal
    const cssLinks = [
      "/assets/portal/vendor/bootstrap/css/bootstrap.min.css",
      "/assets/portal/vendor/bootstrap-icons/bootstrap-icons.css",
      "/assets/portal/vendor/aos/aos.css",
      "/assets/portal/vendor/fontawesome-free/css/all.min.css",
      "/assets/portal/vendor/glightbox/css/glightbox.min.css",
      "/assets/portal/vendor/swiper/swiper-bundle.min.css",
      "/assets/portal/css/main.css",
    ];
    let loadedCount = 0;
    const cssElements = cssLinks.map((href) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      const onDone = () => {
        loadedCount++;
        if (loadedCount >= cssLinks.length) setCssReady(true);
      };
      link.onload = onDone;
      link.onerror = onDone; // contar los que fallen para no bloquear
      document.head.appendChild(link);
      return link;
    });
    return () => {
      scriptElements.forEach((script) => {
        if (document.body.contains(script)) document.body.removeChild(script);
      });
      linkElements.forEach((link) => {
        if (document.head.contains(link)) document.head.removeChild(link);
      });
      cssElements.forEach((link) => {
        if (document.head.contains(link)) document.head.removeChild(link);
      });
    };
  }, []);

  if (!cssReady) return null;

  return (
    <div>
      <div id="header" className="header sticky-top">
        <div className="topbar d-flex align-items-center topbar-custom">
          {/* Elemento decorativo de fondo */}
          <div className="div-decoration" />

          <div className="container d-flex justify-content-center justify-content-md-between header-container">
            <div className="d-none d-md-flex align-items-center top-line">
              <i className="bi bi-building me-2"></i>
              Colegio Profesional de Fonoaudiólogos de La Pampa
            </div>
            <a
              href="https://wa.me/5429542954702284?text=Hola,%20necesito%20información%20sobre%20los%20servicios%20del%20Colegio%20de%20Fonoaudiólogos"
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center wp-link"
            >
              <i className="bi bi-whatsapp me-2"></i>
              Contáctanos por WhatsApp
            </a>
          </div>
        </div>
        <div className="branding d-flex align-items-center">
          <div className="container position-relative d-flex align-items-center justify-content-end">
            <nav id="navmenu" className="navmenu">
              <ul>
                <li>
                  <a
                    href=""
                    className="active"
                    onClick={() => navigate("inicio")}
                  >
                    Inicio
                  </a>
                </li>
                <li className="dropdown">
                  <a href="#" onClick={() => setTabIndex("institucional")}>
                    <span>Institucional</span>{" "}
                    <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    <li>
                      <a href="" onClick={() => navigate("quienes-somos")}>
                        Quiénes somos?
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("comision-directiva")}>
                        Comisión Directiva
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("tribunal-etica")}>
                        Tribunal de Etica y Disciplina
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("comision-revisora")}>
                        Comisión Revisora de Cuentas
                      </a>
                    </li>
                  </ul>
                </li>
                <li className="dropdown">
                  <a href="#" onClick={() => setTabIndex("tramites")}>
                    <span>Trámites</span>{" "}
                    <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    <li>
                      <a href="" onClick={() => navigate("matriculacion")}>
                        Matriculación
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("consultorio")}>
                        Habilitación de Consultorio
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("titulo")}>
                        Título
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("baja-matricula")}>
                        Baja de Matrícula
                      </a>
                    </li>
                  </ul>
                </li>
                <li className="dropdown">
                  <a href="" onClick={() => navigate("ejercicio-profesional")}>
                    <span>Ejercicio Profesional</span>{" "}
                    <i className="bi bi-chevron-down toggle-dropdown"></i>
                  </a>
                  <ul>
                    <li>
                      <a
                        href="/assets/portal/docs/HONORARIOS_MINIMOS_ETICOS.pdf"
                        target="_blank"
                      >
                        Honorarios Mínimos
                      </a>
                    </li>
                    <li>
                      <a
                        href="/assets/portal/docs/CODIGO_DE_ETICA_COFOLP.pdf"
                        target="_blank"
                      >
                        Código de Ética
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("certificado-etica")}>
                        Certificado de Ética
                      </a>
                    </li>
                    <li>
                      <a
                        href="/assets/portal/docs/CONSENTIMIENTO_INFORMADO_COFOLP.pdf"
                        target="_blank"
                      >
                        Consentimiento informado
                      </a>
                    </li>
                    <li>
                      <a href="" onClick={() => navigate("consultorio")}>
                        Habilitación de Consultorio
                      </a>
                    </li>
                  </ul>
                </li>
                <li>
                  <a href="" onClick={() => navigate("profesionales")}>
                    Profesionales
                  </a>
                </li>
                <li>
                  <a href="" onClick={() => navigate("obras-sociales")}>
                    Obras Sociales
                  </a>
                </li>
                <li>
                  <a href="" onClick={() => navigate("contacto")}>
                    Contacto
                  </a>
                </li>
              </ul>
              <i className="mobile-nav-toggle d-xl-none bi bi-list"></i>
            </nav>
            <a className="cta-btn" href="/login">
              AUTOGESTION
            </a>
          </div>
        </div>
      </div>

      <Outlet />

      {/* FOOTER */}
      <footer id="footer" className="footer footer-container">
        <div className="container footer-top">
          <div className="row gy-4">
            <div className="col-lg-4 col-md-6 footer-about">
              <a href="index.html" className="logo d-flex align-items-center">
                <span className="sitename-2">Cofolp</span>
              </a>
              <div className="footer-contact pt-3">
                <p className="line l1">Av. Spinetto 670</p>
                <p className="line l2">Santa Rosa - La Pampa</p>
                <p className="mt-3 line l3">
                  <strong>Teléfono:</strong>
                  <span>+54 2954 702284</span>
                </p>
                <p className="line l4">
                  <strong>Email:</strong>
                  <span>cofolp@gmail.com</span>
                </p>
              </div>
              <div className="social-links d-flex mt-4">
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="twitter-icon"
                >
                  <i className="bi bi-twitter-x"></i>
                </a>
                <a
                  href="https://www.facebook.com/fonos.lapampa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="facebook-icon"
                >
                  <i className="bi bi-facebook"></i>
                </a>
                <a
                  href="https://www.instagram.com/colegio_fonos_lapampa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="instagram-icon"
                >
                  <i className="bi bi-instagram"></i>
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-icon"
                >
                  <i className="bi bi-linkedin"></i>
                </a>
              </div>
            </div>

            <div className="col-lg-2 col-md-3 footer-links">
              <h4 className="links">Links</h4>
              <ul>
                <li>
                  <a className="links" onClick={() => navigate("/")}>
                    <i className="bi bi-chevron-right"></i>
                    Inicio
                  </a>
                </li>
                <li>
                  <a onClick={() => navigate("institucional")}>
                    <i className="bi bi-chevron-right"></i>
                    Institucional
                  </a>
                </li>
                <li>
                  <a href="#" onClick={() => navigate("tramites")}>
                    <i className="bi bi-chevron-right"></i>
                    Trámites
                  </a>
                </li>
                <li>
                  <a onClick={() => navigate("ejercicio-profesional")}>
                    <i className="bi bi-chevron-right"></i>
                    Ejercicio Profesional
                  </a>
                </li>
                <li>
                  <a onClick={() => navigate("profesionales")}>
                    <i className="bi bi-chevron-right"></i>
                    Profesionales
                  </a>
                </li>
              </ul>
            </div>

            <div className="col-lg-2 col-md-3 footer-links">
              <h4 className="ejercicio-profesional">Ejercicio Profesional</h4>
              <ul>
                <li>
                  <a
                    href="/assets/portal/docs/HONORARIOS_MINIMOS_ETICOS.pdf"
                    className="ejercicio-profesional"
                    target="_blank"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Honorarios Mínimos
                  </a>
                </li>
                <li>
                  <a
                    href="/assets/portal/docs/CODIGO_DE_ETICA_COFOLP.pdf"
                    className="ejercicio-profesional"
                    target="_blank"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Código de Etica
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => navigate("certificado-etica")}
                    className="ejercicio-profesional"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Certificado de Etica
                  </a>
                </li>
                <li>
                  <a
                    href="/assets/portal/docs/CONSENTIMIENTO_INFORMADO_COFOLP.pdf"
                    className="ejercicio-profesional"
                    target="_blank"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Consentimiento Informado
                  </a>
                </li>
              </ul>
            </div>

            <div className="col-lg-2 col-md-3 footer-links tramites-links">
              <h4 className="tramites-links">Trámites</h4>
              <ul>
                <li>
                  <a
                    onClick={() => navigate("matriculacion")}
                    className="tramites-links"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Matriculación
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => navigate("consultorio")}
                    className="tramites-links"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Habilitación de Consultorio
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => navigate("titulo")}
                    className="tramites-links"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Título
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => navigate("baja-matricula")}
                    className="tramites-links"
                  >
                    <i className="bi bi-chevron-right"></i>
                    Baja de Matrícula
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="container copyright text-center mt-4">
          <p>
            © <span>Copyright</span>{" "}
            <strong className="px-1 sitename">Cofolp</strong>{" "}
            <span>Todos los derechos reservados</span>
          </p>
          <div className="credits"></div>
        </div>
      </footer>
    </div>
  );
}
